import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Quote, ChevronLeft, ChevronRight, Star } from 'lucide-react';

const Testimonials = ({ testimonials }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!testimonials || testimonials.length === 0) {
    return null;
  }

  const nextTestimonial = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const prevTestimonial = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
  };

  return (
    <section id="testimonials" className="py-24 md:py-32 bg-[#121212] relative overflow-hidden" data-testid="testimonials-section">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-96 h-96 border border-[#d4af37] rounded-full -translate-x-1/2 -translate-y-1/2" />
        <div className="absolute bottom-0 right-0 w-64 h-64 border border-[#d4af37] rounded-full translate-x-1/2 translate-y-1/2" />
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <p className="text-[#d4af37] text-sm tracking-[0.3em] uppercase font-light mb-4">
            Client Stories
          </p>
          <h2 className="font-['Playfair_Display'] text-4xl md:text-5xl lg:text-6xl font-medium text-[#e5e5e5]">
            What They <span className="italic">Say</span>
          </h2>
        </motion.div>

        {/* Testimonial Carousel */}
        <div className="relative max-w-4xl mx-auto">
          <motion.div
            key={currentIndex}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.5 }}
            className="text-center"
          >
            {/* Quote Icon */}
            <Quote className="w-12 h-12 text-[#d4af37] mx-auto mb-8 opacity-50" />

            {/* Stars */}
            <div className="flex justify-center gap-1 mb-6">
              {[...Array(testimonials[currentIndex].rating)].map((_, i) => (
                <Star key={i} className="w-5 h-5 text-[#d4af37] fill-[#d4af37]" />
              ))}
            </div>

            {/* Quote */}
            <p className="font-['Cormorant_Garamond'] text-2xl md:text-3xl lg:text-4xl text-[#e5e5e5] leading-relaxed mb-8 italic">
              "{testimonials[currentIndex].content}"
            </p>

            {/* Author */}
            <div className="flex flex-col items-center">
              <img
                src={testimonials[currentIndex].image}
                alt={testimonials[currentIndex].name}
                className="w-16 h-16 rounded-full object-cover border-2 border-[#d4af37] mb-4"
              />
              <p className="text-[#e5e5e5] font-medium text-lg">
                {testimonials[currentIndex].name}
              </p>
              <p className="text-[#d4af37] text-sm tracking-wider uppercase">
                {testimonials[currentIndex].role}
              </p>
            </div>
          </motion.div>

          {/* Navigation */}
          <div className="flex justify-center items-center gap-8 mt-12">
            <button
              onClick={prevTestimonial}
              className="p-3 border border-[#262626] hover:border-[#d4af37] text-[#a3a3a3] hover:text-[#d4af37] transition-all"
              data-testid="testimonial-prev"
            >
              <ChevronLeft className="w-6 h-6" />
            </button>

            {/* Dots */}
            <div className="flex gap-2">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`w-2 h-2 rounded-full transition-all ${
                    index === currentIndex ? 'bg-[#d4af37] w-8' : 'bg-[#262626]'
                  }`}
                  data-testid={`testimonial-dot-${index}`}
                />
              ))}
            </div>

            <button
              onClick={nextTestimonial}
              className="p-3 border border-[#262626] hover:border-[#d4af37] text-[#a3a3a3] hover:text-[#d4af37] transition-all"
              data-testid="testimonial-next"
            >
              <ChevronRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;
